import { ReactNode, useState } from 'react'
import styled from 'styled-components'
import { REFERRAL_EARNS, referralPath, referralUrl } from './referral'
import { useReferralInfo } from './useReferral'

const Wrap = styled.div`
  display: grid;
  gap: 8px;
  min-width: 0;
`
const Row = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  min-width: 0;
`
const Code = styled.code`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  min-height: 44px;
  border-radius: 12px;
  font-size: 14px;
  overflow-wrap: anywhere;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(127, 212, 232, 0.25);
  color: ${({ theme }) => theme.colors.text};
  font-family: ui-monospace, Menlo, monospace;
`
const Copy = styled.button`
  flex: none;
  min-height: 44px;
  min-width: 84px;
  padding: 0 16px;
  border-radius: 12px;
  border: 0;
  cursor: pointer;
  font-weight: 600;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.invertedContrast ?? '#0b1020'};
  background: ${({ theme }) => theme.colors.primary};
`
const Small = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const Label = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSubtle};
`

/**
 * "Your referral link": the same /r/<handle or wallet> on the profile, the competition pages and the NFT pages, one Copy
 * button and one line of what it earns. `id` may be passed when the caller already knows it (the profile).
 */
export default function MyReferralLink({ wallet, id: known, children, title = 'Your referral link' }: { wallet: string; id?: string; children?: ReactNode; title?: string }) {
  const { info } = useReferralInfo(known ? null : wallet)
  const id = known ?? info?.id ?? wallet
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://apps.cadinu.io'
  const url = referralUrl(origin, id)
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* no clipboard: the link stays selectable */
    }
  }
  return (
    <Wrap data-testid="my-ref">
      <Label>{title}</Label>
      <Row>
        <Code data-testid="my-ref-link" title={url}>
          {origin.replace(/^https?:\/\//, '')}
          {referralPath(id)}
        </Code>
        <Copy type="button" onClick={copy} aria-label="Copy referral link" data-testid="my-ref-copy">
          {copied ? 'Copied' : 'Copy'}
        </Copy>
      </Row>
      <Small data-testid="my-ref-earns">{REFERRAL_EARNS}</Small>
      {children}
    </Wrap>
  )
}
